import { update } from 'state/util';
import { STATUS } from 'state/requestStatus/reducer';

/**
 * Storage for annotations. Both owner and others'. We will
 * drive the show/hide edit & delete buttons via the userId
 * key and whether or not it's the currently authed users'.
 *
 * annotations state sub-tree:
 * {
 *   "candidate-page-id#userId": {
 *     data: [{
 *       text: "some annotation",
 *       id: "020#1524534008.533" <- {sectionId}#{timestamp}
 *     },{
 *     ... some other annotations ...,
 *     ],
 *     status: STATUS.COMPLETE,
 *     message: null || "some status/error message"
 *   },
 *   ... other page annotations ...,
 * }
 *
 * Action Dispatch Structure:
 *  {
 *    type: "LIST_ANNOTATIONS_REQUESTED",
 *    payload: {
 *      pageId: "example-candidate-page",
 *      userId: "my userId",
 *      sectionId: "010",
 *      text: "Some annotation text"
 *    }
 *  }
 */

// we add page keys to this as we add preferences
const initialState = {};

export const annotationsReducer = (state, action) => {
  if (state === undefined)
    return initialState;

  let key = null;
  let currentAnnotations = null;
  if (action.payload && action.payload.userId && action.payload.pageId) {
    key = `${action.payload.pageId}#${action.payload.userId}`;
    currentAnnotations = state[key] ? state[key].data || [] : [];
  }

  switch(action.type) {
    case "LOGOUT_REQUESTED":
      return initialState;

    case "LIST_ANNOTATIONS_REQUESTED":
      return update(state, {
        [key]: {
          message: "Loading...",
          status: STATUS.LOADING,
        },
      });

    case "LIST_ANNOTATIONS_CLEARDATA":
      return update(state, {
        [key]: {
          data: [],
          user: {},
          status: STATUS.LOADING,
        },
      });

    case "LIST_ANNOTATIONS_PAGE_SUCCESSFUL":
      return update(state, {
        [key]: {
          data: currentAnnotations.concat(action.payload.data),
          user: action.payload.user,
          status: STATUS.LOADING,
        },
      });

    case "LIST_ANNOTATIONS_SUCCESSFUL":
      return update(state, {
        [key]: {
          data: currentAnnotations.concat(action.payload.data),
          user: action.payload.user,
          message: null,
          status: STATUS.COMPLETE,
        },
      });

    case "CREATE_ANNOTATION_REQUESTED":
      return update(state, {
        [key]: {
          message: "Creating annotation...",
          status: STATUS.SAVING,
        },
      });

    case "CREATE_ANNOTATION_SUCCESSFUL":
      return update(state, {
        [key]: {
          data: currentAnnotations.concat([action.payload.data]),
          message: "Annotation saved",
          status: STATUS.COMPLETE,
        },
      });

    case "DELETE_ANNOTATION_REQUESTED":
      return update(state, {
        [key]: {
          message: "Deleting annotation...",
          status: STATUS.LOADING,
        },
      });

    case "DELETE_ANNOTATION_SUCCESSFUL":
      const delIndex = currentAnnotations.map((r, i) => {
        if (r.id === action.payload.annotationId)
          return i;
        else
          return 0;
      }).reduce((a, x) => x + a);
      const withoutDeleted = [...currentAnnotations];
      withoutDeleted.splice(delIndex, 1);
      return update(state, {
        [key]: {
          data: withoutDeleted,
          message: "Annotation deleted",
          status: STATUS.COMPLETE,
        },
      });

    case "UPDATE_ANNOTATION_REQUESTED":
      return update(state, {
        [key]: {
          message: "Updating annotation...",
          status: STATUS.SAVING,
        },
      });

    case "UPDATE_ANNOTATION_SUCCESSFUL":
      const updAnnotation = action.payload.data;
      const updIndex = currentAnnotations.map((r, i) => {
        if (r.id === updAnnotation.annotationId)
          return i;
        else
          return 0;
      }).reduce((a, x) => x + a);
      const withUpdated = [...currentAnnotations];
      withUpdated.splice(updIndex, 1, updAnnotation);
      return update(state, {
        [key]: {
          data: withUpdated,
          message: "Annotation saved",
          status: STATUS.COMPLETE,
        },
      });

    case "DELETE_ANNOTATION_FAILED":
    case "CREATE_ANNOTATION_FAILED":
    case "UPDATE_ANNOTATION_FAILED":
    case "LIST_ANNOTATIONS_FAILED":
      const errPageId = action.payload.data.pageId;
      const errUserId = action.payload.data.userId;
      if (action.payload.statusCode === 404) {
        // return a blank vote pref for creating if not found
        return update(state, {
          [`${errPageId}#${errUserId}`]: {
            message: null,
            data: [],
            status: STATUS.NONE,
          },
        });
      }

      return update(state, {
        [key]: {
          message: action.payload.message,
          status: STATUS.FAILED,
        },
      });

    default:
      return state;
  }
};




// WEBPACK FOOTER //
// ./src/state/annotations/reducer.js